import moment from 'moment'
import { DERATE_TYPES, OUTAGE_TYPES, ZERO_CAP_TYPES } from './eventTypeRules'

export { DERATE_TYPES, OUTAGE_TYPES, ZERO_CAP_TYPES }

const HOURS = Array.from({ length: 24 }, (_, i) => i + 1)

export const recalcDrlRow = rows => {
  const ndcRow = rows.find(r => r.rowType === 'metric' && r.metric === 'NDC')
  if (!ndcRow) return rows

  const eventRows = rows.filter(r => r.rowType === 'event')
  const drlRow = rows.find(r => r.rowType === 'metric' && r.metric === 'DRL')
  if (!drlRow) return rows

  const newDrl = { ...drlRow }
  HOURS.forEach(h => {
    const ndcVal = ndcRow[`h${h}`]
    if (ndcVal == null || ndcVal === '') {
      newDrl[`h${h}`] = ''
      return
    }
    const ndc = parseFloat(ndcVal)

    const contributors = []
    let maxAvailCap = null
    eventRows.forEach(er => {
      if (!er.eventData || !DERATE_TYPES.has(er.eventData.eventType)) return
      const capVal = er[`h${h}`]
      if (capVal == null || capVal === '') return
      const v = parseFloat(capVal)
      if (isNaN(v)) return
      contributors.push(`${er.eventData.eventType}: ${v.toFixed(1)}`)
      if (maxAvailCap == null || v > maxAvailCap) maxAvailCap = v
    })

    if (maxAvailCap == null) {
      newDrl[`h${h}`] = 0
      newDrl[`drlTooltip_h${h}`] = `NDC: ${ndc.toFixed(
        1
      )} | No derate events | DRL: 0.0`
      return
    }
    const drl = Math.max(0, ndc - maxAvailCap)
    newDrl[`h${h}`] = drl
    newDrl[`drlTooltip_h${h}`] = `NDC: ${ndc.toFixed(1)} | ${contributors.join(
      ', '
    )} | Max: ${maxAvailCap.toFixed(1)} | DRL = ${ndc.toFixed(
      1
    )} − ${maxAvailCap.toFixed(1)} = ${drl.toFixed(1)}`
  })

  return rows.map(r =>
    r.rowType === 'metric' && r.metric === 'DRL' ? newDrl : r
  )
}

export const calcUnallocatedRow = rows => {
  const ndcRow = rows.find(r => r.rowType === 'metric' && r.metric === 'NDC')
  const netGenRow = rows.find(
    r => r.rowType === 'metric' && r.metric === 'Net Generation'
  )
  const drlRow = rows.find(r => r.rowType === 'metric' && r.metric === 'DRL')
  const unallocRow = rows.find(
    r => r.rowType === 'metric' && r.metric === 'Unallocated'
  )
  if (!ndcRow || !netGenRow || !unallocRow) return rows

  const eventRows = rows.filter(r => r.rowType === 'event')
  const newRow = { ...unallocRow }

  HOURS.forEach(h => {
    const key = `h${h}`
    const ndcVal = ndcRow[key]
    if (ndcVal == null || ndcVal === '') {
      newRow[key] = ''
      return
    }

    const ndc = parseFloat(ndcVal)
    const netGen = parseFloat(netGenRow[key]) || 0

    // Build outage-active segments for this hour across all event rows.
    // Within same minute, sort non-outage first so a resuming derate correctly
    // ends a prior outage segment at that minute.
    const allHourEvents = []
    eventRows.forEach(er => {
      const arr = er.eventDataMap && er.eventDataMap[h]
      if (!Array.isArray(arr) || !arr.length) return
      const isOutageRow = OUTAGE_TYPES.has(er.eventData?.eventType)
      arr.forEach(e =>
        allHourEvents.push({
          min: moment(e.eventStart).minute(),
          isOutage: isOutageRow,
        })
      )
    })
    allHourEvents.sort((a, b) =>
      a.min !== b.min ? a.min - b.min : a.isOutage ? 1 : -1
    )
    const outageSegments = []
    let _outageStart = null
    for (const ev of allHourEvents) {
      if (ev.isOutage && _outageStart == null) {
        _outageStart = ev.min
      } else if (!ev.isOutage && _outageStart != null) {
        outageSegments.push([_outageStart, ev.min])
        _outageStart = null
      }
    }
    if (_outageStart != null) outageSegments.push([_outageStart, 60])

    const nonOutageDuration = (segStart, segEnd) => {
      let free = segEnd - segStart
      for (const [os, oe] of outageSegments) {
        const lo = Math.max(segStart, os)
        const hi = Math.min(segEnd, oe)
        if (hi > lo) free -= hi - lo
      }
      return Math.max(0, free)
    }

    // Tooltip breakdown: one row per segment showing minute range and effective cap
    const derateTableRows = []
    const derateDetails = []
    const sumEvents = eventRows.reduce((sum, er) => {
      const arr = er.eventDataMap && er.eventDataMap[h]
      if (!Array.isArray(arr) || !arr.length) return sum
      const isDerateRow = DERATE_TYPES.has(er.eventData?.eventType)
      const label = er.eventData?.eventType || ''
      const sorted = arr
        .slice()
        .sort(
          (a, b) =>
            moment(a.eventStart).minute() - moment(b.eventStart).minute()
        )
      let rowContrib = 0
      const weighted = sorted.reduce((s, e, i) => {
        const minStart = moment(e.eventStart).minute()
        const rawMinEnd =
          i < sorted.length - 1 ? moment(sorted[i + 1].eventStart).minute() : 60
        const dur = isDerateRow
          ? nonOutageDuration(minStart, rawMinEnd)
          : rawMinEnd - minStart
        if (dur <= 0) return s
        const cap = e.availCap != null ? parseFloat(e.availCap) : 0
        const contrib = cap * (dur / 60)
        rowContrib += contrib
        if (isDerateRow) {
          const mm = s => String(s).padStart(2, '0')
          derateTableRows.push(
            `  ${label} :${mm(minStart)}–:${mm(rawMinEnd)} | eff ${mm(
              dur
            )}min | cap ${cap.toFixed(1)} | = ${contrib.toFixed(1)}`
          )
        }
        return s + contrib
      }, 0)
      if (isDerateRow) derateDetails.push(`${label}: ${rowContrib.toFixed(1)}`)
      return sum + weighted
    }, 0)

    const outageNote =
      outageSegments.length > 0
        ? ` (outage: ${outageSegments
            .map(
              ([s, e]) =>
                `:${String(s).padStart(2, '0')}–:${String(e).padStart(2, '0')}`
            )
            .join(', ')})`
        : ''
    const derateStr =
      derateDetails.length > 0 ? derateDetails.join(', ') + outageNote : 'none'
    const derateTable =
      derateTableRows.length > 0 ? '\n' + derateTableRows.join('\n') : ''

    const drl = drlRow ? parseFloat(drlRow[key]) || 0 : 0
    const unalloc = ndc - netGen - sumEvents - drl
    newRow[key] = unalloc
    newRow[`unallocTooltip_${key}`] =
      `NDC: ${ndc.toFixed(1)} | Net Gen: ${netGen.toFixed(
        1
      )} | Events: ${sumEvents.toFixed(1)} | DRL: ${drl.toFixed(
        1
      )} | Unallocated = ${ndc.toFixed(1)} − ${netGen.toFixed(
        1
      )} − ${sumEvents.toFixed(1)} − ${drl.toFixed(1)} = ${unalloc.toFixed(
        1
      )}` +
      `\nEffective derate: ${derateStr}` +
      derateTable
  })

  return rows.map(r =>
    r.rowType === 'metric' && r.metric === 'Unallocated' ? newRow : r
  )
}

export const calcEventCountRow = rows => {
  const countRow = rows.find(
    r => r.rowType === 'metric' && r.metric === 'Event Count'
  )
  if (!countRow) return rows

  const eventRows = rows.filter(r => r.rowType === 'event')
  const newRow = { ...countRow }

  HOURS.forEach(h => {
    const count = eventRows.reduce((sum, er) => {
      const arr = er.eventDataMap && er.eventDataMap[h]
      return sum + (Array.isArray(arr) ? arr.length : 0)
    }, 0)
    newRow[`h${h}`] = count
  })

  return rows.map(r =>
    r.rowType === 'metric' && r.metric === 'Event Count' ? newRow : r
  )
}

export const calcUnitStatusRow = (rows, unitLockStatus) => {
  const statusRow = rows.find(
    r => r.rowType === 'metric' && r.metric === 'Hourly Status'
  )
  if (!statusRow) return rows

  const eventRows = rows.filter(r => r.rowType === 'event')
  const newRow = { ...statusRow }

  HOURS.forEach(h => {
    let status = unitLockStatus || 'U'
    for (const er of eventRows) {
      const arr = er.eventDataMap && er.eventDataMap[h]
      const e = Array.isArray(arr) ? arr[0] : arr
      if (e && e.isSubmitted != null) {
        status = e.isSubmitted
        break
      }
    }
    newRow[`h${h}`] = status
  })

  return rows.map(r =>
    r.rowType === 'metric' && r.metric === 'Hourly Status' ? newRow : r
  )
}

export const recalcDerivedRows = (rows, unitLockStatus) =>
  calcUnallocatedRow(calcEventCountRow(calcUnitStatusRow(rows, unitLockStatus)))
